import { useMemo } from 'react'

import { sortArrayBy } from '@domain-group/javascript-utils'

import { useLoadMore, useWatchEntityUpdates } from '@domain-group/react-hooks'

import matchFilterNumber from '@functions/utils/matchFilterNumber'

import useReduxAction from '@hooks/useReduxAction'
import useSelector from '@hooks/useSelector'

import type { AdAccountGroupAdAccountRequestOptions } from '@models/types'
import type { ModuleState } from '@redux/modules/types'

const watchEntityKeys = ['adAccountGroupAdAccounts']

type AdAccountGroupAdAccountFilters = {
  adAccountGroupId?: number,
  adAccountId?: number,
}

type UseAdAccountGroupAdAccountsOptions = {
  filters?: AdAccountGroupAdAccountFilters,
  performHttpRequests?: boolean,
  requestOptions?: AdAccountGroupAdAccountRequestOptions,
}

function useAdAccountGroupAdAccounts(options: UseAdAccountGroupAdAccountsOptions) {
  const { filters = {}, requestOptions } = options
  const {
    adAccountGroupId: filterAdAccountGroupId,
    adAccountId: filterAdAccountId,
  } = filters

  const {
    updatedEntities: { adAccountGroupAdAccounts: adAccountGroupAdAccountsUpdatedAt },
  } = useWatchEntityUpdates(watchEntityKeys)

  const { adAccountGroupAdAccounts, adAccounts } = useSelector(reduxState => reduxState.entities)

  const filteredAdAccountGroupAdAccounts = useMemo(() => {
    const filtered = Object.values(adAccountGroupAdAccounts).filter(adAccountGroupAdAccount => {
      const { adAccountGroupId, adAccountId } = adAccountGroupAdAccount

      const matchAdAccount = matchFilterNumber(adAccountId, filterAdAccountId)
      const matchAdAccountGroup = matchFilterNumber(adAccountGroupId, filterAdAccountGroupId)

      return matchAdAccount && matchAdAccountGroup
    })

    return sortArrayBy(filtered, 'asc', ({ adAccountId }) => adAccounts[adAccountId]?.name)
  }, [adAccountGroupAdAccountsUpdatedAt, JSON.stringify(filters)])

  const filteredAdAccountGroupAdAccountsCount = filteredAdAccountGroupAdAccounts.length
  const hasFilteredAdAccountGroupAdAccounts = !!filteredAdAccountGroupAdAccountsCount

  const loadMorePayload = useLoadMore({
    filters,
    loadedCount: filteredAdAccountGroupAdAccountsCount,
    performHttpRequests: options.performHttpRequests,
  })

  const {
    callbacks: { loadMore },
    canLoadMore,
    filtersWithOffset,
    limit,
    performHttpRequests,
  } = loadMorePayload

  const { loading: loadingAdAccountGroupAdAccounts } = useReduxAction(
    'adAccountGroupAdAccounts',
    'loadAdAccountGroupAdAccounts',
    {
      ...requestOptions,
      ...filtersWithOffset,
      limit,
    },
    [filtersWithOffset, performHttpRequests],
    {
      shouldPerformFn: ({ loading }: ModuleState) => performHttpRequests && !loading,
    },
  )

  return {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredAdAccountGroupAdAccounts,
    filteredAdAccountGroupAdAccountsCount,
    hasFilteredAdAccountGroupAdAccounts,
    loading: loadingAdAccountGroupAdAccounts,
  }
}

export default useAdAccountGroupAdAccounts
